import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { Check, ChevronDown, ExternalLink, Loader2, Pencil, Search, Send, ShieldCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getAllCamerasWithSlugs, type CameraPageData } from '@/lib/cameraSlug';
import { generateBrandSlug } from '@/lib/brandSlug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Manufacturer verification panel, /verify/<token>.
 *
 * A brand-scoped, link-gated review surface the founder hands to a
 * manufacturer so they can confirm or correct the specs the catalog
 * publishes for their models. The token comes from
 * scripts/create-manufacturer-token.mjs; all reads and writes go through
 * the manufacturer-panel edge function, and nothing the manufacturer
 * does here touches the public catalog directly (founder applies
 * verdicts to src/data/cameras.ts by hand).
 *
 * Deliberately English-only: this is a B2B tool sent to international
 * vendors, not a public page, so it stays out of the 24-locale i18n tree.
 */

export type Verdict = 'confirmed' | 'corrected' | 'not_ours' | 'discontinued';

export interface VerdictRow {
  model_name: string;
  verdict: Verdict;
  corrections: Record<string, unknown> | null;
  source_url: string | null;
  note: string | null;
  /** Founder-side outcome ('new' | 'applied' | 'dismissed'); server-sent. */
  review_status?: string | null;
  updated_at: string;
}

interface PanelSession {
  brand_slug: string;
  brand_name: string;
  company: string | null;
  expires_at: string;
  finalized_at: string | null;
  verdicts: VerdictRow[];
}

const PAGE_SIZE = 50;

const VERDICT_LABEL: Record<Verdict, string> = {
  confirmed: 'Confirmed correct',
  corrected: 'Correction submitted',
  not_ours: 'Not our product',
  discontinued: 'Discontinued',
};

const VERDICT_BADGE_CLASS: Record<Verdict, string> = {
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-900',
  corrected: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900',
  not_ours: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-900',
  discontinued: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-neutral-900 dark:text-neutral-400 dark:border-neutral-800',
};

type FilterTab = 'all' | 'pending' | 'confirmed' | 'corrected' | 'flagged';

export interface CorrectionDraft {
  hfov: string;
  range: string;
  irRange: string;
  resolution: string;
  focalLength: string;
  type: string;
  sourceUrl: string;
  note: string;
}

export function draftFromCamera(cam: CameraPageData): CorrectionDraft {
  return {
    hfov: String(cam.hfov ?? ''),
    range: String(cam.range ?? ''),
    irRange: String(cam.irRange ?? ''),
    resolution: cam.resolution ?? '',
    focalLength: cam.focalLength ?? '',
    type: cam.type,
    sourceUrl: '',
    note: '',
  };
}

/** Fields actually changed vs the catalog row; empty object = no change. */
export function diffDraft(cam: CameraPageData, d: CorrectionDraft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const num = (s: string): number | null => {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  };
  const hfov = num(d.hfov);
  if (hfov !== null && hfov !== cam.hfov) out.hfov = hfov;
  const range = num(d.range);
  if (range !== null && range !== cam.range) out.range = range;
  const irRange = num(d.irRange);
  if (irRange !== null && irRange !== cam.irRange) out.irRange = irRange;
  const res = d.resolution.trim();
  if (res && res !== (cam.resolution ?? '')) out.resolution = res;
  const focal = d.focalLength.trim();
  if (focal && focal !== (cam.focalLength ?? '')) out.focalLength = focal;
  if (d.type && d.type !== cam.type) out.type = d.type;
  return out;
}

/**
 * Snapshot of the catalog values the reviewer is looking at. Sent with
 * every verdict so the attestation is pinned to a spec state instead of
 * a bare model name (ONVIF/Milestone-style version pinning).
 */
export function specSnapshot(cam: CameraPageData): Record<string, unknown> {
  const out: Record<string, unknown> = {
    hfov: cam.hfov, range: cam.range, irRange: cam.irRange, type: cam.type,
  };
  if (cam.resolution) out.resolution = cam.resolution;
  if (cam.focalLength) out.focalLength = cam.focalLength;
  return out;
}

/**
 * Client-side mirror of the server's normaliseCorrections whitelist, so
 * a value the server would reject or silently drop is caught here with
 * a precise message instead of a generic save failure. Also normalizes
 * the Unicode multiplication sign datasheets love into a plain 'x'.
 */
export function buildCorrections(
  cam: CameraPageData,
  d: CorrectionDraft,
): { corrections: Record<string, unknown> } | { error: string } {
  const normalized: CorrectionDraft = { ...d, resolution: d.resolution.replace(/[×X]/g, 'x').trim() };
  const out = diffDraft(cam, normalized);
  if (Object.keys(out).length === 0) {
    return { error: 'No field differs from the current catalog values. Use "Confirm correct" if the data is right.' };
  }
  if (typeof out.hfov === 'number' && (out.hfov < 0.1 || out.hfov > 360)) {
    return { error: 'Horizontal FOV must be between 0.1 and 360 degrees.' };
  }
  if (typeof out.range === 'number' && (out.range < 0 || out.range > 20000)) {
    return { error: 'Effective range must be between 0 and 20000 m.' };
  }
  if (typeof out.irRange === 'number' && (out.irRange < 0 || out.irRange > 2000)) {
    return { error: 'IR range must be between 0 and 2000 m.' };
  }
  if (typeof out.resolution === 'string' && !/^\d{2,5}x\d{2,5}$/.test(out.resolution)) {
    return { error: 'Resolution must look like 3840x2160 (width x height in pixels).' };
  }
  if (typeof out.focalLength === 'string' && out.focalLength.length > 50) {
    return { error: 'Focal length text is too long (max 50 characters).' };
  }
  return { corrections: out };
}

const inputClass = 'h-9 text-sm';

/** Machine-readable error code out of a FunctionsHttpError response. */
export async function errCodeOf(e: unknown): Promise<string> {
  try {
    const ctx = (e as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      const j = await ctx.json();
      if (j && typeof j.error === 'string') return j.error;
    }
  } catch {
    // Body unreadable: fall through to the generic code.
  }
  return 'unknown';
}

export default function ManufacturerVerifyPanel() {
  const { token = '' } = useParams<{ token: string }>();
  const pwStorageKey = `mfg-verify-pw:${token}`;

  // The access password rides along on every edge-function call. It is
  // kept per tab (sessionStorage) so a reload does not re-prompt but a
  // forwarded URL alone is useless without it.
  const [password, setPassword] = useState<string>(() => {
    try { return sessionStorage.getItem(pwStorageKey) ?? ''; } catch { return ''; }
  });
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSubmitting, setPwSubmitting] = useState(false);

  // Who at the manufacturer is reviewing. Self-declared once per tab
  // and attached to every verdict for attribution and follow-up.
  const reviewerStorageKey = `mfg-verify-reviewer:${token}`;
  const [reviewer, setReviewer] = useState<{ name: string; email: string } | null>(() => {
    try {
      const raw = sessionStorage.getItem(reviewerStorageKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [nameInput, setNameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [identityError, setIdentityError] = useState<string | null>(null);

  // Brand context shown on the gate before any password is entered,
  // fetched via the passwordless preflight action.
  const [brandPreview, setBrandPreview] = useState<{
    brand_name: string; company: string | null; expires_at: string;
  } | null>(null);

  const [status, setStatus] = useState<'password' | 'identity' | 'loading' | 'invalid' | 'error' | 'ready'>(
    () => (password ? 'loading' : 'password'),
  );
  // Password of the most recent load attempt, for the retry button on
  // the transient-error screen (loadSession only persists it on success).
  const lastPwRef = useRef('');
  const [session, setSession] = useState<PanelSession | null>(null);
  const [verdictMap, setVerdictMap] = useState<Map<string, VerdictRow>>(new Map());

  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<FilterTab>('all');
  const [page, setPage] = useState(1);

  // Per-row UI state
  const [savingModels, setSavingModels] = useState<Set<string>>(new Set());
  const [editingModel, setEditingModel] = useState<string | null>(null);
  const [revisingModels, setRevisingModels] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState<CorrectionDraft | null>(null);

  // Finalize dialog
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizeEmail, setFinalizeEmail] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  const [reportSent, setReportSent] = useState(false);

  const invoke = useCallback(
    async (body: Record<string, unknown>): Promise<Record<string, unknown>> => {
      const { data, error } = await supabase.functions.invoke('manufacturer-panel', {
        body: { token, password, ...body },
      });
      if (error) throw new Error(await errCodeOf(error));
      return (data ?? {}) as Record<string, unknown>;
    },
    [token, password],
  );

  /** Drop the stored password and return to the gate. */
  const backToGate = useCallback((message: string | null) => {
    try { sessionStorage.removeItem(pwStorageKey); } catch { /* private mode */ }
    setPassword('');
    setPwError(message);
    setStatus('password');
  }, [pwStorageKey]);

  const loadSession = useCallback(async (pw: string) => {
    lastPwRef.current = pw;
    setStatus('loading');
    try {
      const { data, error } = await supabase.functions.invoke('manufacturer-panel', {
        body: { token, password: pw, action: 'session' },
      });
      if (error) throw new Error(await errCodeOf(error));
      const s = (data ?? {}) as PanelSession;
      setSession(s);
      setVerdictMap(new Map(s.verdicts.map(v => [v.model_name, v])));
      setReportSent(Boolean(s.finalized_at));
      setPassword(pw);
      try { sessionStorage.setItem(pwStorageKey, pw); } catch { /* private mode */ }
      let hasReviewer = false;
      try { hasReviewer = Boolean(sessionStorage.getItem(reviewerStorageKey)); } catch { /* private mode */ }
      setStatus(hasReviewer ? 'ready' : 'identity');
    } catch (e) {
      const code = e instanceof Error ? e.message : 'unknown';
      if (code === 'password_required' || code === 'invalid_password') {
        backToGate(code === 'invalid_password'
          ? 'Wrong password. Check the code you received from CCTVplanner and try again.'
          : null);
      } else if (code === 'too_many_attempts') {
        backToGate('Too many attempts. Please wait 15 minutes before trying again.');
      } else if (code === 'Invalid link') {
        // Only an explicit server rejection means the link is dead.
        setStatus('invalid');
      } else {
        // Network blips, outages and 5xx are transient: offer a retry
        // instead of telling the manufacturer their link was revoked.
        setStatus('error');
      }
    }
  }, [token, pwStorageKey, reviewerStorageKey, backToGate]);

  useEffect(() => {
    if (password) {
      void loadSession(password);
      return;
    }
    // No stored password: personalize the gate. A dead link is detected
    // here too, so the visitor is not asked for a password pointlessly.
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('manufacturer-panel', {
          body: { token, action: 'preflight' },
        });
        if (error) throw new Error(await errCodeOf(error));
        const p = data as { brand_name?: string } | null;
        if (p?.brand_name) {
          setBrandPreview(p as { brand_name: string; company: string | null; expires_at: string });
        }
      } catch (e) {
        const code = e instanceof Error ? e.message : 'unknown';
        // Network hiccups keep the gate usable; a rejected token does not.
        if (code === 'Invalid link') setStatus('invalid');
      }
    })();
    // Mount-only by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePasswordSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const pw = pwInput.trim();
    if (!pw || pwSubmitting) return;
    setPwSubmitting(true);
    setPwError(null);
    await loadSession(pw);
    setPwSubmitting(false);
  };

  const handleIdentitySubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const name = nameInput.trim();
    const email = emailInput.trim();
    if (!name) { setIdentityError('Please enter your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setIdentityError('Please enter a valid work email address.');
      return;
    }
    const r = { name, email };
    setReviewer(r);
    try { sessionStorage.setItem(reviewerStorageKey, JSON.stringify(r)); } catch { /* private mode */ }
    setIdentityError(null);
    setStatus('ready');
  };

  const models = useMemo(() => {
    if (!session) return [] as CameraPageData[];
    return getAllCamerasWithSlugs()
      .filter(c => generateBrandSlug(c.manufacturer) === session.brand_slug)
      .sort((a, b) => a.model.localeCompare(b.model));
  }, [session]);

  const counts = useMemo(() => {
    const c = { confirmed: 0, corrected: 0, not_ours: 0, discontinued: 0 };
    for (const v of verdictMap.values()) c[v.verdict] += 1;
    const reviewed = c.confirmed + c.corrected + c.not_ours + c.discontinued;
    return { ...c, reviewed, pending: Math.max(0, models.length - reviewed) };
  }, [verdictMap, models.length]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return models.filter(m => {
      if (q && !m.model.toLowerCase().includes(q)) return false;
      const v = verdictMap.get(m.model)?.verdict;
      switch (tab) {
        case 'pending': return !v;
        case 'confirmed': return v === 'confirmed';
        case 'corrected': return v === 'corrected';
        case 'flagged': return v === 'not_ours' || v === 'discontinued';
        default: return true;
      }
    });
  }, [models, search, tab, verdictMap]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageModels = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const markSaving = (names: string[], on: boolean) => {
    setSavingModels(prev => {
      const next = new Set(prev);
      for (const n of names) { if (on) next.add(n); else next.delete(n); }
      return next;
    });
  };

  const submitVerdicts = useCallback(
    async (items: Array<{ model_name: string; verdict: Verdict; corrections?: Record<string, unknown>; source_url?: string; note?: string; baseline?: Record<string, unknown> }>) => {
      const names = items.map(i => i.model_name);
      markSaving(names, true);
      try {
        await invoke({
          action: 'submit',
          items,
          ...(reviewer ? { reviewer_name: reviewer.name, reviewer_email: reviewer.email } : {}),
        });
        const now = new Date().toISOString();
        setVerdictMap(prev => {
          const next = new Map(prev);
          for (const i of items) {
            next.set(i.model_name, {
              model_name: i.model_name,
              verdict: i.verdict,
              corrections: i.corrections ?? null,
              source_url: i.source_url ?? null,
              note: i.note ?? null,
              updated_at: now,
            });
          }
          return next;
        });
        setRevisingModels(prev => {
          const next = new Set(prev);
          for (const n of names) next.delete(n);
          return next;
        });
        return true;
      } catch (e) {
        const code = e instanceof Error ? e.message : 'unknown';
        if (code === 'password_required' || code === 'invalid_password') {
          backToGate('Please re-enter the access password.');
          toast.error('Access check failed. Please sign in again.');
        } else {
          toast.error('Saving failed. Please check your connection and try again.');
        }
        return false;
      } finally {
        markSaving(names, false);
      }
    },
    [invoke, backToGate, reviewer],
  );

  const handleSimpleVerdict = (cam: CameraPageData, verdict: Verdict) => {
    void submitVerdicts([{ model_name: cam.model, verdict, baseline: specSnapshot(cam) }]);
  };

  const handleConfirmPagePending = () => {
    const pending = pageModels.filter(m => !verdictMap.get(m.model)).map(m => ({
      model_name: m.model,
      verdict: 'confirmed' as Verdict,
      baseline: specSnapshot(m),
    }));
    if (pending.length === 0) return;
    void submitVerdicts(pending).then(ok => {
      if (ok) toast.success(`Confirmed ${pending.length} models`);
    });
  };

  const openCorrection = (cam: CameraPageData) => {
    setEditingModel(cam.model);
    const existing = verdictMap.get(cam.model);
    const base = draftFromCamera(cam);
    if (existing?.verdict === 'corrected' && existing.corrections) {
      const c = existing.corrections;
      setDraft({
        ...base,
        hfov: c.hfov != null ? String(c.hfov) : base.hfov,
        range: c.range != null ? String(c.range) : base.range,
        irRange: c.irRange != null ? String(c.irRange) : base.irRange,
        resolution: typeof c.resolution === 'string' ? c.resolution : base.resolution,
        focalLength: typeof c.focalLength === 'string' ? c.focalLength : base.focalLength,
        type: typeof c.type === 'string' ? c.type : base.type,
        sourceUrl: existing.source_url ?? '',
        note: existing.note ?? '',
      });
    } else {
      setDraft(base);
    }
  };

  const saveCorrection = (cam: CameraPageData) => {
    if (!draft) return;
    const built = buildCorrections(cam, draft);
    if ('error' in built) {
      toast.error(built.error);
      return;
    }
    const sourceUrl = draft.sourceUrl.trim();
    if (!/^https?:\/\//i.test(sourceUrl) || sourceUrl.length > 500) {
      toast.error('A datasheet or product page URL is required (max 500 characters).');
      return;
    }
    const note = draft.note.trim();
    if (note.length > 2000) {
      toast.error('The note is too long (max 2000 characters).');
      return;
    }
    void submitVerdicts([{
      model_name: cam.model,
      verdict: 'corrected',
      corrections: built.corrections,
      source_url: sourceUrl,
      note: note || undefined,
      baseline: specSnapshot(cam),
    }]).then(ok => {
      if (ok) { setEditingModel(null); setDraft(null); }
    });
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      await invoke({
        action: 'finalize',
        ...(finalizeEmail.trim() ? { contact_email: finalizeEmail.trim() } : {}),
      });
      setReportSent(true);
      setFinalizeOpen(false);
      toast.success('Report submitted. Thank you for verifying your data.');
    } catch (e) {
      const code = e instanceof Error ? e.message : 'unknown';
      if (code === 'password_required' || code === 'invalid_password') {
        setFinalizeOpen(false);
        backToGate('Please re-enter the access password.');
      } else {
        toast.error('Submitting the report failed. Your per-model answers are saved; please try again.');
      }
    } finally {
      setFinalizing(false);
    }
  };

  // ---- Render states ------------------------------------------------

  if (status === 'password') {
    return (
      <PanelChrome brandName={brandPreview?.brand_name} company={brandPreview?.company}>
        <div className="max-w-md mx-auto py-24 px-4">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Restricted access
          </p>
          <h1 className="text-xl font-semibold mb-2">
            {brandPreview
              ? `${brandPreview.brand_name} verification workspace`
              : 'Manufacturer verification workspace'}
          </h1>
          {brandPreview?.company && (
            <p className="text-sm text-muted-foreground mb-2">Prepared for {brandPreview.company}</p>
          )}
          <p className="text-sm text-muted-foreground mb-6">
            {brandPreview
              ? `Review and confirm the specifications CCTVplanner publishes for ${brandPreview.brand_name} cameras. `
              : ''}
            Enter the access password you received from CCTVplanner separately from this link.
            The link alone does not grant access.
          </p>
          <form onSubmit={handlePasswordSubmit} className="space-y-3">
            <Input
              type="password"
              autoFocus
              autoComplete="off"
              value={pwInput}
              onChange={e => setPwInput(e.target.value)}
              placeholder="XXXX-XXXX-XXXX"
              className="h-10 font-mono tracking-widest"
              aria-label="Access password"
            />
            {pwError && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">{pwError}</p>
            )}
            <Button type="submit" className="w-full h-10 gap-1.5" disabled={pwSubmitting || !pwInput.trim()}>
              {pwSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Open workspace
            </Button>
          </form>
          <div className="text-xs text-muted-foreground mt-6 space-y-1.5">
            {brandPreview?.expires_at && (
              <p>
                Access valid until {new Date(brandPreview.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.
              </p>
            )}
            <p>
              No password? Ask your CCTVplanner contact or write to{' '}
              <a href="mailto:contact@cctvplanner.io" className="underline underline-offset-4">
                contact@cctvplanner.io
              </a>.
            </p>
          </div>
        </div>
      </PanelChrome>
    );
  }

  if (status === 'identity' && session) {
    return (
      <PanelChrome brandName={session.brand_name} company={session.company}>
        <div className="max-w-md mx-auto py-24 px-4">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Before you start
          </p>
          <h1 className="text-xl font-semibold mb-3">Who is reviewing?</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Your verdicts will be recorded on behalf of {session.brand_name}. Please introduce
            yourself so our engineers can reach you with follow-up questions about corrections.
          </p>
          <form onSubmit={handleIdentitySubmit} className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="mfg-reviewer-name">Full name</label>
              <Input
                id="mfg-reviewer-name"
                autoFocus
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                placeholder="Jane Doe"
                className="h-10"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="mfg-reviewer-email">Work email</label>
              <Input
                id="mfg-reviewer-email"
                type="email"
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                placeholder="you@company.com"
                className="h-10"
              />
              <p className="text-xs text-muted-foreground">Please use your company email address.</p>
            </div>
            {identityError && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">{identityError}</p>
            )}
            <Button type="submit" className="w-full h-10">
              Continue to the model list
            </Button>
          </form>
        </div>
      </PanelChrome>
    );
  }

  if (status === 'loading') {
    return (
      <PanelChrome>
        <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mb-3" />
          <p className="text-sm">Opening your verification workspace...</p>
        </div>
      </PanelChrome>
    );
  }

  if (status === 'error') {
    return (
      <PanelChrome brandName={brandPreview?.brand_name}>
        <div className="max-w-md mx-auto py-24 px-4 text-center">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Connection</p>
          <h1 className="text-xl font-semibold mb-3">We could not load your workspace</h1>
          <p className="text-sm text-muted-foreground mb-6">
            This looks like a temporary network or server problem, not an issue with your link.
            Your saved verdicts are safe.
          </p>
          <Button className="h-10 px-6" onClick={() => void loadSession(lastPwRef.current || password)}>
            Try again
          </Button>
        </div>
      </PanelChrome>
    );
  }

  if (status === 'invalid' || !session) {
    return (
      <PanelChrome>
        <div className="max-w-md mx-auto py-24 px-4 text-center">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Access</p>
          <h1 className="text-xl font-semibold mb-3">This verification link is not active</h1>
          <p className="text-sm text-muted-foreground mb-6">
            The link may have expired or been revoked. Please contact us and we will issue a fresh one.
          </p>
          <a href="mailto:contact@cctvplanner.io" className="text-sm underline underline-offset-4">
            contact@cctvplanner.io
          </a>
        </div>
      </PanelChrome>
    );
  }

  const progressPct = models.length > 0 ? Math.round((counts.reviewed / models.length) * 100) : 0;
  const pendingOnPage = pageModels.filter(m => !verdictMap.get(m.model)).length;

  const tabs: Array<{ key: FilterTab; label: string; count: number }> = [
    { key: 'all', label: 'All', count: models.length },
    { key: 'pending', label: 'Pending', count: counts.pending },
    { key: 'confirmed', label: 'Confirmed', count: counts.confirmed },
    { key: 'corrected', label: 'Corrected', count: counts.corrected },
    { key: 'flagged', label: 'Flagged', count: counts.not_ours + counts.discontinued },
  ];

  return (
    <PanelChrome brandName={session.brand_name} company={session.company}>
      <div className="max-w-5xl mx-auto px-4 pb-40">
        {/* Intro */}
        <section className="py-8 border-b border-border">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
            Manufacturer verification
          </p>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-3">
            {session.brand_name}: {models.length.toLocaleString()} models in the CCTVplanner catalog
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            These specifications drive coverage simulations for thousands of security installers
            and are published on each model&apos;s page. Please review them: confirm what is right,
            correct what is wrong (a datasheet link is required), and flag anything that is not
            your product or no longer sold. Every answer is saved instantly; nothing goes live
            without our editorial review.
          </p>
        </section>

        {/* Progress */}
        <section className="py-5 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              {counts.reviewed.toLocaleString()} of {models.length.toLocaleString()} models reviewed
            </span>
            <span className="text-sm text-muted-foreground">{progressPct}%</span>
          </div>
          <div className="h-2 bg-muted overflow-hidden">
            <div className="h-full bg-emerald-600 transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          {reportSent && (
            <p className="mt-3 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              A report has been submitted to CCTVplanner. You can keep reviewing and submit again.
            </p>
          )}
        </section>

        {/* Toolbar */}
        <section className="py-4 flex flex-col md:flex-row md:items-center gap-3 sticky top-0 bg-background z-10 border-b border-border">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search model number..."
              className="pl-9 h-9 text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {tabs.map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => { setTab(t.key); setPage(1); }}
                className={
                  t.key === tab
                    ? 'px-3 py-1.5 text-xs font-medium border border-foreground bg-foreground text-background transition-colors'
                    : 'px-3 py-1.5 text-xs font-medium border border-border text-muted-foreground hover:border-foreground transition-colors'
                }
              >
                {t.label} <span className="opacity-60">({t.count.toLocaleString()})</span>
              </button>
            ))}
          </div>
        </section>

        {/* Batch action */}
        {pendingOnPage > 1 && (
          <div className="py-3 flex justify-end">
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleConfirmPagePending}>
              <Check className="w-3.5 h-3.5" />
              Confirm all {pendingOnPage} pending on this page
            </Button>
          </div>
        )}

        {/* Model list */}
        {models.length === 0 ? (
          <div className="py-20 text-center text-sm text-muted-foreground">
            No catalog models found for this brand. Please contact us at{' '}
            <a href="mailto:contact@cctvplanner.io" className="underline underline-offset-4">contact@cctvplanner.io</a>.
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-sm text-muted-foreground">
            No models match the current search and filter.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {pageModels.map(cam => (
              <ModelRow
                key={cam.slug}
                cam={cam}
                verdict={verdictMap.get(cam.model) ?? null}
                saving={savingModels.has(cam.model)}
                editing={editingModel === cam.model}
                revising={revisingModels.has(cam.model)}
                draft={editingModel === cam.model ? draft : null}
                onDraftChange={setDraft}
                onConfirm={() => handleSimpleVerdict(cam, 'confirmed')}
                onFlag={(v) => handleSimpleVerdict(cam, v)}
                onOpenCorrection={() => openCorrection(cam)}
                onCancelCorrection={() => { setEditingModel(null); setDraft(null); }}
                onSaveCorrection={() => saveCorrection(cam)}
                onRevise={() => setRevisingModels(prev => new Set(prev).add(cam.model))}
              />
            ))}
          </ul>
        )}

        {/* Pagination */}
        {pageCount > 1 && (
          <nav className="flex items-center justify-between py-6" aria-label="Pagination">
            <Button
              variant="outline" size="sm" className="h-8 text-xs"
              disabled={safePage <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {safePage} of {pageCount} · {filtered.length.toLocaleString()} models
            </span>
            <Button
              variant="outline" size="sm" className="h-8 text-xs"
              disabled={safePage >= pageCount}
              onClick={() => setPage(p => Math.min(pageCount, p + 1))}
            >
              Next
            </Button>
          </nav>
        )}
      </div>

      {/* Sticky submit bar */}
      {counts.reviewed > 0 && (
        <div className="fixed bottom-0 inset-x-0 border-t border-border bg-background/95 backdrop-blur z-20">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground hidden sm:block">
              {counts.confirmed} confirmed · {counts.corrected} corrected · {counts.not_ours + counts.discontinued} flagged
            </p>
            <Button
              size="sm"
              className="h-9 text-xs gap-1.5 ml-auto"
              onClick={() => {
                setFinalizeEmail(prev => prev || reviewer?.email || '');
                setFinalizeOpen(true);
              }}
            >
              <Send className="w-3.5 h-3.5" />
              Submit report to CCTVplanner
            </Button>
          </div>
        </div>
      )}

      {/* Finalize dialog */}
      <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Submit your verification report</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1 text-sm">
            <p className="text-muted-foreground">
              We will receive a summary of your review ({counts.reviewed.toLocaleString()} models:
              {' '}{counts.confirmed} confirmed, {counts.corrected} corrections,
              {' '}{counts.not_ours + counts.discontinued} flagged) and apply the changes after
              editorial review. You can continue reviewing afterwards and submit again at any time.
            </p>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="mfg-contact-email">
                Contact email for follow-up questions (optional)
              </label>
              <Input
                id="mfg-contact-email"
                type="email"
                value={finalizeEmail}
                onChange={e => setFinalizeEmail(e.target.value)}
                placeholder="you@company.com"
                className={inputClass}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setFinalizeOpen(false)} disabled={finalizing}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleFinalize} disabled={finalizing} className="gap-1.5">
              {finalizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Submit report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PanelChrome>
  );
}

// ---- Chrome (header/footer shared by all panel states) --------------

export function PanelChrome({
  children, brandName, company,
}: { children: React.ReactNode; brandName?: string; company?: string | null }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Helmet>
        <title>{brandName ? `${brandName} catalog verification | CCTVplanner` : 'Catalog verification | CCTVplanner'}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-semibold tracking-tight">CCTVplanner</Link>
            <span className="text-border">|</span>
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Verification portal
            </span>
          </div>
          {brandName && (
            <div className="text-right">
              <p className="text-sm font-medium leading-tight">{brandName}</p>
              {company && <p className="text-xs text-muted-foreground leading-tight">{company}</p>}
            </div>
          )}
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-border">
        <div className="max-w-5xl mx-auto px-4 py-6 text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-2">
          <span>CCTVplanner.io manufacturer verification portal</span>
          <a href="mailto:contact@cctvplanner.io" className="underline underline-offset-4">
            contact@cctvplanner.io
          </a>
        </div>
      </footer>
    </div>
  );
}

// ---- One model row --------------------------------------------------

const TYPE_OPTIONS = ['bullet', 'dome', 'ptz', 'panoramic', 'turret'] as const;

export interface ModelRowProps {
  cam: CameraPageData;
  verdict: VerdictRow | null;
  saving: boolean;
  editing: boolean;
  revising: boolean;
  draft: CorrectionDraft | null;
  onDraftChange: (d: CorrectionDraft) => void;
  onConfirm: () => void;
  onFlag: (v: Verdict) => void;
  onOpenCorrection: () => void;
  onCancelCorrection: () => void;
  onSaveCorrection: () => void;
  onRevise: () => void;
}

export const ModelRow = React.memo(function ModelRow({
  cam, verdict, saving, editing, revising, draft, onDraftChange,
  onConfirm, onFlag, onOpenCorrection, onCancelCorrection, onSaveCorrection, onRevise,
}: ModelRowProps) {
  const showActions = !verdict || revising;

  const specBits = [
    cam.resolution,
    cam.focalLength,
    `${cam.hfov}° HFOV`,
    cam.irRange > 0 ? `IR ${cam.irRange} m` : 'no IR',
  ].filter(Boolean);

  return (
    <li className="py-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{cam.model}</span>
            <span className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 border border-border text-muted-foreground">
              {cam.type}
            </span>
            <a
              href={cam.path}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Open the public page for this model"
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{specBits.join(' · ')}</p>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {saving ? (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground px-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving
            </span>
          ) : showActions ? (
            <>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={onConfirm}>
                <Check className="w-3 h-3" /> Confirm correct
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={onOpenCorrection}>
                <Pencil className="w-3 h-3" /> Correct
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs px-1.5">
                    <ChevronDown className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onFlag('not_ours')} className="text-xs">
                    Not our product
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onFlag('discontinued')} className="text-xs">
                    Discontinued model
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <span className={`text-[11px] font-medium px-2 py-1 border ${VERDICT_BADGE_CLASS[verdict!.verdict]}`}>
                {VERDICT_LABEL[verdict!.verdict]}
                {verdict!.review_status === 'applied' && ' · accepted'}
                {verdict!.review_status === 'dismissed' && ' · declined'}
              </span>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={onRevise}>
                Revise
              </Button>
            </>
          )}
        </div>
      </div>

      {editing && draft && (
        <div className="mt-3 border border-border bg-muted/30 p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
            Propose corrected specifications
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
            <LabeledField label="Horizontal FOV (degrees)">
              <Input inputMode="decimal" value={draft.hfov} className={inputClass}
                onChange={e => onDraftChange({ ...draft, hfov: e.target.value })} />
            </LabeledField>
            <LabeledField label="Effective range (m)">
              <Input inputMode="decimal" value={draft.range} className={inputClass}
                onChange={e => onDraftChange({ ...draft, range: e.target.value })} />
            </LabeledField>
            <LabeledField label="IR range (m, 0 = no IR)">
              <Input inputMode="decimal" value={draft.irRange} className={inputClass}
                onChange={e => onDraftChange({ ...draft, irRange: e.target.value })} />
            </LabeledField>
            <LabeledField label="Resolution (e.g. 3840x2160)">
              <Input value={draft.resolution} className={inputClass}
                onChange={e => onDraftChange({ ...draft, resolution: e.target.value })} />
            </LabeledField>
            <LabeledField label="Focal length (e.g. 2.8-12mm)">
              <Input value={draft.focalLength} className={inputClass}
                onChange={e => onDraftChange({ ...draft, focalLength: e.target.value })} />
            </LabeledField>
            <LabeledField label="Form factor">
              <select
                value={draft.type}
                onChange={e => onDraftChange({ ...draft, type: e.target.value })}
                className="h-9 w-full border border-input bg-background px-3 text-sm"
              >
                {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </LabeledField>
          </div>
          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <LabeledField label="Datasheet or product page URL (required)">
              <Input value={draft.sourceUrl} placeholder="https://..." className={inputClass}
                onChange={e => onDraftChange({ ...draft, sourceUrl: e.target.value })} />
            </LabeledField>
            <LabeledField label="Note (optional)">
              <Textarea value={draft.note} rows={1} className="text-sm min-h-9"
                onChange={e => onDraftChange({ ...draft, note: e.target.value })} />
            </LabeledField>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onCancelCorrection}>
              Cancel
            </Button>
            <Button size="sm" className="h-8 text-xs" onClick={onSaveCorrection} disabled={saving}>
              Save correction
            </Button>
          </div>
        </div>
      )}
    </li>
  );
});

function LabeledField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
